import { defineMessages, defineMessage } from 'react-intl';

import { ENVIRONMENT } from '../../../constants';
import { MEDIA_SEGMENT_EDIT_TYPE } from '../../../model/media/constants';
import {
  MEDIA_SEGMENT_EDIT_LAYOUTS,
  SHOT_BACKGROUND_TYPES,
  SHOT_LAYOUT_SIZES,
  SHOT_TRANSFORMATION_TYPE,
} from '../constants';
import { SVGS } from '../../shared/icons/constants';

export const EPISODE_CREATOR_ADVERTISER_HELP_URL =
  ENVIRONMENT.EPISODE_CREATOR_ADVERTISER_HELP_URL;
export const EPISODE_CREATOR_PUBLISHER_HELP_URL =
  ENVIRONMENT.EPISODE_CREATOR_PUBLISHER_HELP_URL;

export const EPISODE_CREATOR_ADVERTISER_PRODUCT_UPDATES_URL =
  'https://businesshelp.snapchat.com/en-US/article/publisher-updates';
export const EPISODE_CREATOR_PUBLISHER_PRODUCT_UPDATES_URL =
  'https://publish-snapchat.zendesk.com/hc/en-us/articles/360035633833-Episode-Creation-Product-Updates';

export const SHOT_VIEW_SLIDER_INPUT_SIZE = '5';
export const SHOT_VIEW_RATIO_SLIDER_STEP = 1;

export const MAX_SCALE_ALLOWED = 10;
export const MIN_SCALE_ALLOWED = 0;

export const SHOT_LAYOUT_LABELS = defineMessages({
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_2]: {
    defaultMessage: 'Split-Screen',
    description: 'Label for Split-Screen shot layout type',
  },
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_3]: {
    defaultMessage: 'Three-Screen',
    description: 'Label for Three-Screen shot layout type',
  },
  [MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP]: {
    defaultMessage: 'Full-Screen',
    description: 'Label for Full-Screen shot layout type',
  },
  [MEDIA_SEGMENT_EDIT_TYPE.LETTERBOX]: {
    defaultMessage: 'Letterbox',
    description: 'Label for Letterbox shot layout type',
  },
});

export const SHOT_LAYOUT_FRAME_LABELS = {
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_2]: [
    defineMessage({
      defaultMessage: 'Top Screen',
      description: 'Label for Top Screen in layout properties',
    }),
    defineMessage({
      defaultMessage: 'Bottom Screen',
      description: 'Label for Bottom Screen in layout properties',
    }),
  ],
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_3]: [
    defineMessage({
      defaultMessage: 'Top Screen',
      description: 'Label for Top Screen in layout properties',
    }),
    defineMessage({
      defaultMessage: 'Middle Screen',
      description: 'Label for Middle Screen in layout properties',
    }),
    defineMessage({
      defaultMessage: 'Bottom Screen',
      description: 'Label for Bottom Screen in layout properties',
    }),
  ],
  [MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP]: [
    defineMessage({
      defaultMessage: 'Full-Screen Layout',
      description: 'Label for Full-Screen layout properties',
    }),
  ],
  [MEDIA_SEGMENT_EDIT_TYPE.LETTERBOX]: [
    defineMessage({
      defaultMessage: 'Wide Screen',
      description: 'Label for Wide Screen option in Letterbox Properties',
    }),
  ],
};

export const SHOT_LAYOUT_SIZE_LABELS = defineMessages({
  [SHOT_LAYOUT_SIZES.CUSTOM]: {
    defaultMessage: 'Custom',
    description: 'Label for custom layout size',
  },
  [SHOT_LAYOUT_SIZES.RATIO_1_1]: {
    defaultMessage: '1:1',
    description: 'Label for 1 to 1 ratio layout size',
  },
  [SHOT_LAYOUT_SIZES.RATIO_1_2]: {
    defaultMessage: '1:2',
    description: 'Label for 1 to 2 ratio layout size',
  },
  [SHOT_LAYOUT_SIZES.RATIO_2_1]: {
    defaultMessage: '2:1',
    description: 'Label for 2 to 1 ratio layout size',
  },
  [SHOT_LAYOUT_SIZES.SQUARE]: {
    defaultMessage: 'Square',
    description: 'Label for square layout size',
  },
  [SHOT_LAYOUT_SIZES.TALL]: {
    defaultMessage: 'Tall',
    description: 'Label for tall layout size',
  },
  [SHOT_LAYOUT_SIZES.WIDE]: {
    defaultMessage: 'Wide',
    description: 'Label for wide layout size',
  },
});

export const SHOT_LAYOUT_BACKGROUND_TYPE_LABELS = defineMessages({
  [SHOT_BACKGROUND_TYPES.BLUR]: {
    defaultMessage: 'Blur',
    description: 'Label for blur property',
  },
  [SHOT_BACKGROUND_TYPES.FILL]: {
    defaultMessage: 'Fill',
    description: 'Label for fill property',
  },
});

export const SHOT_LAYOUT_TYPE_OPTIONS = [
  {
    icon: SVGS.layoutstatic,
    label: SHOT_LAYOUT_LABELS[MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP],
    value: MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP,
  },
  {
    icon: SVGS.layoutsplitvertical2,
    label: SHOT_LAYOUT_LABELS[MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_2],
    value: MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_2,
  },
  {
    icon: SVGS.layoutsplitvertical3,
    label: SHOT_LAYOUT_LABELS[MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_3],
    value: MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_3,
  },
  {
    icon: SVGS.layoutletterbox,
    label: SHOT_LAYOUT_LABELS[MEDIA_SEGMENT_EDIT_TYPE.LETTERBOX],
    value: MEDIA_SEGMENT_EDIT_TYPE.LETTERBOX,
  },
];

export const SHOT_LAYOUT_SIZE_OPTIONS = {
  [MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP]: [
    {
      label: SHOT_LAYOUT_SIZE_LABELS[SHOT_LAYOUT_SIZES.CUSTOM],
      value: MEDIA_SEGMENT_EDIT_LAYOUTS.STATIC_CROP.CUSTOM,
    },
  ],
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_2]: [
    {
      label: SHOT_LAYOUT_SIZE_LABELS[SHOT_LAYOUT_SIZES.RATIO_2_1],
      value: MEDIA_SEGMENT_EDIT_LAYOUTS.SPLIT_VERTICAL_2.RATIO_2_1,
    },
    {
      label: SHOT_LAYOUT_SIZE_LABELS[SHOT_LAYOUT_SIZES.RATIO_1_2],
      value: MEDIA_SEGMENT_EDIT_LAYOUTS.SPLIT_VERTICAL_2.RATIO_1_2,
    },
    {
      label: SHOT_LAYOUT_SIZE_LABELS[SHOT_LAYOUT_SIZES.RATIO_1_1],
      value: MEDIA_SEGMENT_EDIT_LAYOUTS.SPLIT_VERTICAL_2.RATIO_1_1,
    },
    {
      hotkeyDisabled: true,
      label: SHOT_LAYOUT_SIZE_LABELS[SHOT_LAYOUT_SIZES.CUSTOM],
      value: MEDIA_SEGMENT_EDIT_LAYOUTS.SPLIT_VERTICAL_2.CUSTOM,
    },
  ],
  [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_3]: [
    {
      label: SHOT_LAYOUT_SIZE_LABELS[SHOT_LAYOUT_SIZES.CUSTOM],
      value: MEDIA_SEGMENT_EDIT_LAYOUTS.SPLIT_VERTICAL_3.CUSTOM,
    },
  ],
  [MEDIA_SEGMENT_EDIT_TYPE.LETTERBOX]: [
    {
      label: SHOT_LAYOUT_SIZE_LABELS[SHOT_LAYOUT_SIZES.WIDE],
      value: MEDIA_SEGMENT_EDIT_LAYOUTS.LETTERBOX.WIDE,
    },
    {
      label: SHOT_LAYOUT_SIZE_LABELS[SHOT_LAYOUT_SIZES.SQUARE],
      value: MEDIA_SEGMENT_EDIT_LAYOUTS.LETTERBOX.SQUARE,
    },
    {
      label: SHOT_LAYOUT_SIZE_LABELS[SHOT_LAYOUT_SIZES.TALL],
      value: MEDIA_SEGMENT_EDIT_LAYOUTS.LETTERBOX.TALL,
    },
    {
      hotkeyDisabled: true,
      label: SHOT_LAYOUT_SIZE_LABELS[SHOT_LAYOUT_SIZES.CUSTOM],
      value: MEDIA_SEGMENT_EDIT_LAYOUTS.LETTERBOX.CUSTOM,
    },
  ],
};

export const MIXED_COLORS_DEFAULT_COLOR = '#ffffff';
export const MIXED_SHOT_EDITOR_PROPERTY_INDICATOR_STRING = '—';
export const DECIMAL_PRECISION_FOR_COMPARISON_OF_TRANSLATION_VALUES = 1;
export const DECIMAL_PRECISION_FOR_COMPARISON_OF_SCALE_VALUES = 3;

export const SHOT_TRANSFORMATION_TYPE_LABELS = defineMessages({
  [SHOT_TRANSFORMATION_TYPE.PAN]: {
    defaultMessage: 'PAN AND ZOOM',
    description: 'Label for Pan and Zoom transform effect',
  },
  [SHOT_TRANSFORMATION_TYPE.STATIC]: {
    defaultMessage: 'STATIC',
    description: 'Label for static transform',
  },
});

// Ordering in this array maps 1:1 in UI
export const SHOT_TRANSFORMATION_TYPES = [
  SHOT_TRANSFORMATION_TYPE.STATIC,
  SHOT_TRANSFORMATION_TYPE.PAN,
];
